import { Component, type ErrorInfo, type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import type { Doc, Id } from '../../../convex/functions/_generated/dataModel';
import { adminSession } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import { fmtUsdMicros, fmtPeriod } from '@/lib/money';

/**
 * /dashboard/creators/:id — creator detail.
 *
 * One reactive api.admin.creators.detail query powers the whole page:
 * identity + funnel, shareable links (card + affiliate), the manual
 * post registry, every agreement version (history is append-only
 * server-side), and bonus payout rows. ensureLinks is fired once on
 * mount to mint the affiliate code if the creator never opened their
 * referral screen — the reactive query picks the new code up itself.
 */

type Campaign = Doc<'creatorCampaigns'>;
type Agreement = Doc<'creatorAgreements'>;
type Payout = Doc<'creatorBonusPayouts'>;
type Status = Campaign['status'];

const STRUCTURE_LABEL: Record<Agreement['structure'], string> = {
  fixed_monthly: 'Fixed monthly',
  per_post: 'Per post',
  hybrid: 'Hybrid (monthly + per post)',
  revenue_share: 'Revenue share',
};

const PLATFORMS = ['tiktok', 'instagram', 'snapchat', 'facebook', 'youtube'] as const;

export function CreatorDetailPage() {
  return (
    <DetailErrorBoundary>
      <DetailInner />
    </DetailErrorBoundary>
  );
}

function DetailInner() {
  const { id } = useParams<{ id: string }>();
  const token = adminSession.getToken() ?? '';
  const campaignId = id as Id<'creatorCampaigns'>;
  const data = useQuery(api.admin.creators.detail, id ? { token, campaignId } : 'skip');
  const ensureLinks = useMutation(api.admin.creators.ensureLinks);

  // Mint the affiliate code if absent; no-op when it exists.
  useEffect(() => {
    if (!id) return;
    ensureLinks({ token, campaignId }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!data) {
    return <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>Loading…</div>;
  }

  const { campaign, rollup, links, posts, agreements, payouts } = data;
  const currentAgreement = agreements.find((a) => a.supersededAt == null) ?? null;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/dashboard/creators" style={{ color: 'var(--ink-3)', fontSize: 13, textDecoration: 'none' }}>
          ← Creator campaigns
        </Link>
      </div>

      <header style={{ marginBottom: 20 }}>
        <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
          {campaign.name}
        </h2>
        <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 4 }}>
          <span className="mono" style={{ fontSize: 11 }}>{campaign.handle}</span>
          {' · '}{campaign.platform}{' · '}
          <span className="mono" style={{ fontSize: 11, textTransform: 'uppercase' }}>{campaign.metro}</span>
          {' · '}<StatusBadge status={campaign.status} />
        </div>
      </header>

      <LinksCard links={links} />

      <Section title="Funnel">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatChip label="Visits" value={rollup.visits.toLocaleString()} />
          <StatChip label="Ratings" value={rollup.ratings.toLocaleString()} />
          <StatChip label="Signups" value={rollup.signups.toLocaleString()} strong />
          <StatChip label="CPI" value={rollup.cpiUsd != null ? `$${rollup.cpiUsd.toFixed(2)}` : '—'} />
          <StatChip label="Base fee" value={`$${campaign.feeUsd.toLocaleString()}`} />
        </div>
      </Section>

      <PostsCard campaignId={campaign._id} posts={posts} />

      <AgreementCard
        campaignId={campaign._id}
        agreement={currentAgreement}
        payouts={payouts}
        signupCount={rollup.signups}
      />

      <StatementsCard campaignId={campaign._id} />

      <AgreementHistory agreements={agreements} payouts={payouts} signupCount={rollup.signups} />
    </div>
  );
}

// ─── Revenue-share statements ──────────────────────────────────

/**
 * StatementsCard — the monthly payout surface.
 *
 * Payouts are settled BY HAND: the close cron produces a statement, the
 * admin pays out of band (bank / PayPal / Wise) and records the reference
 * here. There is no payment rail, and deliberately so.
 *
 * The provisional row at the top is the CURRENT month, still accruing. It is
 * visually separated from settled statements because it will keep changing
 * until the close runs — presenting it like a statement would invite paying
 * against a number that isn't final.
 */
function StatementsCard({ campaignId }: { campaignId: Id<'creatorCampaigns'> }) {
  const token = adminSession.getToken() ?? '';
  const data = useQuery(api.admin.creators.listStatements, { token, campaignId });
  const preview = useQuery(api.admin.creators.currentPeriodPreview, { token, campaignId });
  const paidMut = useMutation(api.admin.creators.markStatementPaid);
  const [ref, setRef] = useState<Record<string, string>>({});

  const statements = data?.statements ?? [];

  return (
    <Section title="Revenue share">
      {preview && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
            border: '1px dashed var(--line)', borderRadius: 10, marginBottom: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {fmtPeriod(preview.periodKey)} · {fmtUsdMicros(preview.creatorUsdMicros)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
              Provisional — still accruing, closes on the 3rd of next month.
              {' '}{preview.count.toLocaleString()} item{preview.count === 1 ? '' : 's'}.
              {preview.truncated && ' Showing the first 500.'}
            </div>
          </div>
        </div>
      )}

      {statements.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          No closed statements yet. The first one appears after the period-close
          job runs (3rd of the month, for the month before).
        </div>
      )}

      {statements.map((s) => {
        const payable = s.payableUsdMicros;
        const canPay = s.status === 'final';
        return (
          <div
            key={s._id}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 0', borderTop: '1px solid var(--line)',
            }}
          >
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {fmtPeriod(s.periodKey)} · {fmtUsdMicros(payable)}
                <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>
                  {' '}({(s.sharePercentBps / 100).toFixed(2)}% of net)
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                {s.chargeCount} charge{s.chargeCount === 1 ? '' : 's'}
                {s.refundCount > 0 && ` · ${s.refundCount} refund${s.refundCount === 1 ? '' : 's'}`}
                {' · gross '}{fmtUsdMicros(s.grossUsdMicros)}
                {s.carriedInUsdMicros < 0 && ` · carried in ${fmtUsdMicros(s.carriedInUsdMicros)}`}
                {s.paidAt && ` · paid ${new Date(s.paidAt).toLocaleDateString()}`}
                {s.paymentRef && ` · ref ${s.paymentRef}`}
              </div>

              {/* The two things that make a number surprising, surfaced rather
                  than buried: how much of it rests on an estimated store
                  commission, and how much on a probabilistic attribution
                  match. Both are answerable to a creator who disputes it. */}
              {(s.estimatedCommissionCount > 0 || s.probabilisticCount > 0 || s.lateAccrualCount > 0) && (
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
                  {s.estimatedCommissionCount > 0 &&
                    `${s.estimatedCommissionCount} row(s) used an estimated store commission. `}
                  {s.probabilisticCount > 0 &&
                    `${s.probabilisticCount} attributed by probabilistic match, not confirmed. `}
                  {s.lateAccrualCount > 0 &&
                    `${s.lateAccrualCount} item(s) carried in from earlier periods.`}
                </div>
              )}

              {s.carriedOutUsdMicros < 0 && (
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
                  Refunds outran earnings by {fmtUsdMicros(-s.carriedOutUsdMicros)} — paid $0.00 and
                  carried forward{s.carryChainLength >= 3 ? ` (${s.carryChainLength} periods running)` : ''}.
                </div>
              )}
            </div>

            {canPay && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <input
                  value={ref[s._id] ?? ''}
                  onChange={(e) => setRef((m) => ({ ...m, [s._id]: e.target.value }))}
                  placeholder="Payment ref"
                  style={{
                    padding: '6px 8px', borderRadius: 8, border: '1px solid var(--line)',
                    background: 'transparent', color: 'inherit', fontSize: 12, width: 120,
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  type="button"
                  onClick={() =>
                    paidMut({ token, statementId: s._id, paymentRef: ref[s._id] || undefined }).catch(() => {})
                  }
                  style={primaryBtn}
                >
                  Mark paid
                </button>
              </div>
            )}
            {s.status === 'paid' && (
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Paid</span>
            )}
            {s.status === 'void' && (
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Void</span>
            )}
            {s.status === 'building' && (
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Closing…</span>
            )}
          </div>
        );
      })}
    </Section>
  );
}

// ─── Links ─────────────────────────────────────────────────────

function LinksCard({
  links,
}: {
  links: {
    card: string;
    signup?: string | null;
    install?: string | null;
    affiliate: string | null;
    referralCode: string | null;
  };
}) {
  // `signup` is the new canonical link-in-bio; `affiliate` is kept as its
  // alias so this renders correctly against a backend that hasn't been
  // redeployed yet.
  const signup = links.signup ?? links.affiliate;
  return (
    <Section title="Links">
      <LinkRow
        label="Link-in-bio (give creators this one)"
        url={signup ?? 'Minting…'}
        copyValue={signup ?? undefined}
      />
      <LinkRow
        label="Direct install"
        url={links.install ?? 'Minting…'}
        copyValue={links.install ?? undefined}
      />
      <LinkRow label="Card" url={links.card} />
      <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 8 }}>
        <strong>Prefer the link-in-bio one.</strong> It lands on the site with the creator&rsquo;s code
        attached, so signing up there stamps the creator onto the <em>account</em> — the user can
        install and subscribe weeks later, on any device, and it still credits correctly. Exact on
        both platforms.
        <br />
        <br />
        <strong>Direct install</strong> skips the site and goes to the store. Attribution then depends
        on matching the install back to the click: exact on Android, but only a probabilistic match on
        iOS, where Apple provides no way to carry a code through the App Store. Use it for audiences
        already sold on the app, and expect some under-crediting.
        <br />
        <br />
        The card attributes via its slug. A code beats a slug when both are present, and attribution
        is single-shot per user.
      </div>
    </Section>
  );
}

function LinkRow({ label, url, copyValue }: { label: string; url: string; copyValue?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const value = copyValue ?? url;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the URL is visible to select manually */ }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
      <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', width: 170, flexShrink: 0 }}>
        {label}
      </span>
      {/* 180px basis: on a narrow screen the URL + Copy wrap under the label. */}
      <code style={{ flex: '1 1 180px', minWidth: 0, fontSize: 12, background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 8, padding: '7px 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {url}
      </code>
      <button type="button" onClick={copy} style={ghostBtn} disabled={!copyValue && !url}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

// ─── Posts ─────────────────────────────────────────────────────

function PostsCard({ campaignId, posts }: { campaignId: Id<'creatorCampaigns'>; posts: Doc<'creatorPosts'>[] }) {
  const token = adminSession.getToken() ?? '';
  const addMut = useMutation(api.admin.creators.addPost);
  const removeMut = useMutation(api.admin.creators.removePost);
  const [platform, setPlatform] = useState<string>('tiktok');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await addMut({ token, campaignId, platform, url, note: note.trim() || undefined });
      setUrl(''); setNote('');
    } catch (err) {
      setError(friendlyError(err, 'addPost'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={`Posts (${posts.length})`}>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: posts.length ? 14 : 0 }}>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={{ ...input, width: 130 }}>
          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.tiktok.com/@handle/video/…" style={{ ...input, flex: 2, minWidth: 220 }} required />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" style={{ ...input, flex: 1, minWidth: 120 }} />
        <button type="submit" disabled={busy} style={primaryBtn}>{busy ? 'Adding…' : 'Add post'}</button>
      </form>
      {error && <div style={{ color: '#a8331f', fontSize: 13, marginBottom: 10 }}>{error}</div>}

      {posts.length > 0 && (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>Platform</th>
              <th style={th}>URL</th>
              <th style={th}>Note</th>
              <th style={th}>Added</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p._id}>
                <td style={td}><span className="mono" style={{ fontSize: 11 }}>{p.platform}</span></td>
                <td style={td}>
                  {/* Table cells ignore max-width, so the ellipsis lives on the link. */}
                  <a href={p.url} target="_blank" rel="noreferrer" style={{ color: 'var(--coral, #e86a4c)', fontSize: 12, display: 'block', maxWidth: 'min(340px, 60vw)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.url}</a>
                </td>
                <td style={{ ...td, color: 'var(--ink-3)', fontSize: 12 }}>{p.note ?? '—'}</td>
                <td style={{ ...td, color: 'var(--ink-3)', fontSize: 12 }}>{new Date(p.createdAt).toLocaleDateString()}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button type="button" onClick={() => removeMut({ token, postId: p._id }).catch(() => {})} style={ghostBtn}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

// ─── Current agreement + bonuses ───────────────────────────────

function AgreementCard({
  campaignId,
  agreement,
  payouts,
  signupCount,
}: {
  campaignId: Id<'creatorCampaigns'>;
  agreement: Agreement | null;
  payouts: Payout[];
  signupCount: number;
}) {
  const [formOpen, setFormOpen] = useState(false);

  return (
    <Section title={agreement ? `Current agreement — v${agreement.version}` : 'Current agreement'}>
      {!agreement ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 12 }}>
          No agreement on file yet. Create v1 below.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <StatChip label="Structure" value={STRUCTURE_LABEL[agreement.structure]} />
            {agreement.fixedMonthlyUsd != null && (
              <StatChip label="Monthly" value={`$${agreement.fixedMonthlyUsd.toLocaleString()}`} />
            )}
            {agreement.perPostUsd != null && (
              <StatChip label="Per post" value={`$${agreement.perPostUsd.toLocaleString()}`} />
            )}
            <StatChip label="Since" value={new Date(agreement.createdAt).toLocaleDateString()} />
          </div>
          {agreement.notes && (
            <div style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 12 }}>{agreement.notes}</div>
          )}

          {agreement.bonuses.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                Bonuses
              </div>
              {agreement.bonuses.map((b, i) => (
                <BonusRow
                  key={i}
                  agreementId={agreement._id}
                  index={i}
                  bonus={b}
                  payout={payouts.find((p) => p.agreementId === agreement._id && p.bonusIndex === i) ?? null}
                  signupCount={signupCount}
                />
              ))}
            </div>
          )}
        </>
      )}

      {formOpen ? (
        <NewAgreementForm campaignId={campaignId} onDone={() => setFormOpen(false)} />
      ) : (
        <button type="button" onClick={() => setFormOpen(true)} style={primaryBtn}>
          {agreement ? 'New version (supersede)' : 'Create agreement v1'}
        </button>
      )}
    </Section>
  );
}

function BonusRow({
  agreementId,
  index,
  bonus,
  payout,
  signupCount,
}: {
  agreementId: Id<'creatorAgreements'>;
  index: number;
  bonus: Agreement['bonuses'][number];
  payout: Payout | null;
  signupCount: number;
}) {
  const token = adminSession.getToken() ?? '';
  const hitMut = useMutation(api.admin.creators.markBonusHit);
  const paidMut = useMutation(api.admin.creators.markBonusPaid);

  const progress = bonus.metric === 'signups' ? Math.min(1, signupCount / bonus.threshold) : null;
  const state = payout?.status ?? (progress != null && progress >= 1 ? 'hit' : 'progress');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid var(--line)' }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          ${bonus.amountUsd.toLocaleString()} at {bonus.threshold.toLocaleString()} {bonus.metric}
          {bonus.note && <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}> — {bonus.note}</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
          {bonus.metric === 'signups'
            ? `${signupCount.toLocaleString()} / ${bonus.threshold.toLocaleString()} signups`
            : 'Views tracked externally — mark hit when the creator reports them'}
          {payout && ` · hit ${new Date(payout.hitAt).toLocaleDateString()}`}
          {payout?.paidAt && ` · paid ${new Date(payout.paidAt).toLocaleDateString()}`}
        </div>
        {progress != null && (
          <div style={{ height: 4, background: 'var(--bg)', borderRadius: 4, marginTop: 6, maxWidth: 260 }}>
            <div style={{ height: 4, width: `${progress * 100}%`, background: 'var(--coral, #e86a4c)', borderRadius: 4 }} />
          </div>
        )}
      </div>
      {state === 'progress' && bonus.metric === 'views' && (
        <button type="button" onClick={() => hitMut({ token, agreementId, bonusIndex: index }).catch(() => {})} style={ghostBtn}>
          Mark hit
        </button>
      )}
      {state === 'hit' && payout && (
        <button type="button" onClick={() => paidMut({ token, payoutId: payout._id }).catch(() => {})} style={primaryBtn}>
          Mark paid
        </button>
      )}
      {state === 'paid' && (
        <span className="mono" style={{ fontSize: 10, color: '#2e7d32', letterSpacing: 1, textTransform: 'uppercase' }}>Paid</span>
      )}
      {state === 'progress' && bonus.metric === 'signups' && (
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>In progress</span>
      )}
    </div>
  );
}

// ─── New agreement form ────────────────────────────────────────

function NewAgreementForm({ campaignId, onDone }: { campaignId: Id<'creatorCampaigns'>; onDone: () => void }) {
  const token = adminSession.getToken() ?? '';
  const createMut = useMutation(api.admin.creators.createAgreement);
  const [structure, setStructure] = useState<Agreement['structure']>('fixed_monthly');
  const [monthly, setMonthly] = useState('');
  const [perPost, setPerPost] = useState('');
  const [bonuses, setBonuses] = useState<Array<{ metric: 'signups' | 'views'; threshold: string; amountUsd: string; note: string }>>([]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Revenue share is ORTHOGONAL to structure — a creator can hold a retainer
  // AND a share — so it's an independent toggle rather than a fourth option
  // in the select. Choosing "Revenue share" as the structure just means a
  // PURE share deal, and forces the toggle on.
  const isPureRevShare = structure === 'revenue_share';
  const [revShareOn, setRevShareOn] = useState(false);
  const [sharePercent, setSharePercent] = useState('20');
  const [paidPayments, setPaidPayments] = useState('1');
  const [backstopMonths, setBackstopMonths] = useState('24');
  const [includeConsumables, setIncludeConsumables] = useState(false);
  const [includeWebBilling, setIncludeWebBilling] = useState(true);
  const revShareEnabled = revShareOn || isPureRevShare;

  const needsMonthly = !isPureRevShare && structure !== 'per_post';
  const needsPerPost = !isPureRevShare && structure !== 'fixed_monthly';

  const setBonus = (i: number, patch: Partial<(typeof bonuses)[number]>) => {
    setBonuses((prev) => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await createMut({
        token,
        campaignId,
        structure,
        fixedMonthlyUsd: needsMonthly ? Number(monthly) : undefined,
        perPostUsd: needsPerPost ? Number(perPost) : undefined,
        revenueShare: revShareEnabled
          ? {
              // The form takes a human percent; the ledger stores basis
              // points, because a float rate multiplying money reintroduces
              // exactly the drift the micro-USD ledger exists to avoid.
              percentBps: Math.round(Number(sharePercent) * 100),
              paidPayments: Number(paidPayments),
              backstopMonths: Number(backstopMonths),
              basis: 'net' as const,
              includeConsumables,
              includeWebBilling,
            }
          : undefined,
        bonuses: bonuses.map((b) => ({
          metric: b.metric,
          threshold: Number(b.threshold),
          amountUsd: Number(b.amountUsd),
          note: b.note.trim() || undefined,
        })),
        notes: notes.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError(friendlyError(err, 'createAgreement'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ borderTop: '1px solid var(--line)', paddingTop: 14, marginTop: 4 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={fieldLabel}>
          <span>Structure</span>
          <select value={structure} onChange={(e) => setStructure(e.target.value as Agreement['structure'])} style={input}>
            <option value="fixed_monthly">Fixed monthly</option>
            <option value="per_post">Per post</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </label>
        {needsMonthly && (
          <label style={fieldLabel}>
            <span>Monthly (USD)</span>
            <input value={monthly} onChange={(e) => setMonthly(e.target.value)} inputMode="decimal" placeholder="500" style={input} required />
          </label>
        )}
        {needsPerPost && (
          <label style={fieldLabel}>
            <span>Per post (USD)</span>
            <input value={perPost} onChange={(e) => setPerPost(e.target.value)} inputMode="decimal" placeholder="150" style={input} required />
          </label>
        )}
        <label style={{ ...fieldLabel, flex: 1, minWidth: 200 }}>
          <span>Notes (optional)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="2 videos + 1 story per month…" style={input} />
        </label>
      </div>

      {/* Revenue share — independent of structure, so a retainer creator can
          also earn a share. Forced on for a pure revenue_share deal. */}
      <div style={{ borderTop: '1px solid var(--line)', marginTop: 12, paddingTop: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={revShareEnabled}
            disabled={isPureRevShare}
            onChange={(e) => setRevShareOn(e.target.checked)}
          />
          <span>Revenue share{isPureRevShare && ' (required for this structure)'}</span>
        </label>

        {revShareEnabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
            <label style={fieldLabel}>
              <span>Share of net (%)</span>
              <input
                value={sharePercent}
                onChange={(e) => setSharePercent(e.target.value)}
                inputMode="decimal"
                placeholder="20"
                style={input}
                required
              />
            </label>
            <label style={fieldLabel}>
              <span>Paid on first N payments</span>
              <input
                value={paidPayments}
                onChange={(e) => setPaidPayments(e.target.value)}
                inputMode="numeric"
                placeholder="1"
                style={input}
                required
              />
            </label>
            <label style={fieldLabel}>
              <span>Backstop (months from first charge)</span>
              <input
                value={backstopMonths}
                onChange={(e) => setBackstopMonths(e.target.value)}
                inputMode="numeric"
                placeholder="24"
                style={input}
                required
              />
            </label>
            <label style={{ ...fieldLabel, justifyContent: 'flex-end' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={includeWebBilling} onChange={(e) => setIncludeWebBilling(e.target.checked)} />
                Include web billing
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={includeConsumables} onChange={(e) => setIncludeConsumables(e.target.checked)} />
                Include one-off purchases
              </span>
            </label>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', flexBasis: '100%' }}>
              Paid on NET revenue — gross minus store commission, tax and refunds — on the
              first {paidPayments || '1'} subscription payment{paidPayments === '1' ? '' : 's'}{' '}
              each attributed user makes. Free premium from referrals never counts; only a
              real charge does. A refunded payment frees its slot. Settled monthly in
              arrears.
              <br />
              <strong>Note:</strong> payments, not months — {paidPayments || '1'}{' '}
              payment{paidPayments === '1' ? '' : 's'} is worth far more on a yearly plan
              (~${(279.99 * Number(paidPayments || 1)).toFixed(0)} gross) than weekly
              (~${(9.99 * Number(paidPayments || 1)).toFixed(0)}).
            </div>
          </div>
        )}
      </div>

      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
        Bonuses
      </div>
      {bonuses.map((b, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
          <select value={b.metric} onChange={(e) => setBonus(i, { metric: e.target.value as 'signups' | 'views' })} style={{ ...input, flex: '1 1 90px', minWidth: 0, maxWidth: 110 }}>
            <option value="signups">signups</option>
            <option value="views">views</option>
          </select>
          <input value={b.threshold} onChange={(e) => setBonus(i, { threshold: e.target.value })} placeholder="Threshold" inputMode="numeric" style={{ ...input, flex: '1 1 90px', minWidth: 0, maxWidth: 110 }} required />
          <input value={b.amountUsd} onChange={(e) => setBonus(i, { amountUsd: e.target.value })} placeholder="$ bonus" inputMode="decimal" style={{ ...input, flex: '1 1 80px', minWidth: 0, maxWidth: 100 }} required />
          <input value={b.note} onChange={(e) => setBonus(i, { note: e.target.value })} placeholder="Note (optional)" style={{ ...input, flex: 1, minWidth: 140 }} />
          <button type="button" onClick={() => setBonuses((prev) => prev.filter((_, j) => j !== i))} style={ghostBtn}>✕</button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setBonuses((prev) => [...prev, { metric: 'signups', threshold: '', amountUsd: '', note: '' }])}
        style={{ ...ghostBtn, marginBottom: 12 }}
      >
        + Add bonus
      </button>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={busy} style={primaryBtn}>
          {busy ? 'Saving…' : 'Save new version'}
        </button>
        <button type="button" onClick={onDone} style={ghostBtn}>Cancel</button>
      </div>
      {error && <div style={{ marginTop: 10, color: '#a8331f', fontSize: 13 }}>{error}</div>}
    </form>
  );
}

// ─── Agreement history ─────────────────────────────────────────

function AgreementHistory({ agreements, payouts, signupCount }: { agreements: Agreement[]; payouts: Payout[]; signupCount: number }) {
  if (agreements.length === 0) return null;
  return (
    <Section title={`Agreement history (${agreements.length})`}>
      {agreements.map((a) => {
        const isCurrent = a.supersededAt == null;
        return (
          <div key={a._id} style={{ borderTop: '1px solid var(--line)', padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>v{a.version}</span>
              <span style={{ fontSize: 13 }}>{STRUCTURE_LABEL[a.structure]}</span>
              {a.fixedMonthlyUsd != null && <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>${a.fixedMonthlyUsd}/mo</span>}
              {a.perPostUsd != null && <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>${a.perPostUsd}/post</span>}
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {new Date(a.createdAt).toLocaleDateString()}
                {a.supersededAt != null && ` → superseded ${new Date(a.supersededAt).toLocaleDateString()}`}
              </span>
              {isCurrent ? (
                <span className="mono" style={{ fontSize: 10, color: '#2e7d32', letterSpacing: 1, textTransform: 'uppercase' }}>Current</span>
              ) : (
                <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>Superseded</span>
              )}
            </div>
            {a.notes && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{a.notes}</div>}
            {a.bonuses.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {a.bonuses.map((b, i) => {
                  const payout = payouts.find((p) => p.agreementId === a._id && p.bonusIndex === i);
                  return (
                    <div key={i} style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                      ${b.amountUsd.toLocaleString()} at {b.threshold.toLocaleString()} {b.metric}
                      {payout && ` — ${payout.status}${payout.paidAt ? ` ${new Date(payout.paidAt).toLocaleDateString()}` : ''}`}
                      {!payout && !isCurrent && ' — not hit'}
                      {!payout && isCurrent && b.metric === 'signups' && ` — ${signupCount.toLocaleString()}/${b.threshold.toLocaleString()}`}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

// ─── Shared bits ───────────────────────────────────────────────

function StatusBadge({ status }: { status: Status }) {
  const color = status === 'active' ? '#2e7d32' : status === 'booked' ? '#a86b00' : status === 'cancelled' ? '#a8331f' : 'var(--ink-3)';
  return (
    <span className="mono" style={{ fontSize: 10, color, letterSpacing: 1, textTransform: 'uppercase' }}>{status}</span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 16 }}>
        {children}
      </div>
    </section>
  );
}

function StatChip({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'baseline', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '8px 12px' }}>
      <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontWeight: strong ? 800 : 700 }}>{value}</span>
    </div>
  );
}

const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 11,
  color: 'var(--ink-3)',
  textTransform: 'uppercase',
  letterSpacing: 1,
  fontFamily: 'var(--font-mono, monospace)',
};

const tbl: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--line-strong)',
  fontWeight: 600, color: 'var(--ink-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1,
};
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--line)', color: 'var(--ink)' };
const input: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 10, border: '1px solid var(--line)',
  background: 'var(--bg)', color: 'var(--ink)', fontSize: 13, fontFamily: 'inherit',
};
const primaryBtn: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 10, border: 'none', background: 'var(--coral, #e86a4c)',
  color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
};
const ghostBtn: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 10, border: '1px solid var(--line)', background: 'transparent',
  color: 'var(--ink-3)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
};

// Same rationale as Health.tsx: useQuery re-throws on render, so the
// only safe inline-error path is a class ErrorBoundary.
interface DetailErrorState { err: unknown | null }

class DetailErrorBoundary extends Component<{ children: ReactNode }, DetailErrorState> {
  state: DetailErrorState = { err: null };
  static getDerivedStateFromError(err: unknown): DetailErrorState {
    return { err };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[CreatorDetailPage]', error, info);
  }
  render() {
    if (this.state.err !== null) {
      return (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '14px 16px', borderRadius: 12, fontSize: 13 }}>
          {friendlyError(this.state.err, 'creatorDetail')}
        </div>
      );
    }
    return this.props.children;
  }
}
